import type { FC, ReactNode } from 'react'
import type { Article, Confidentiality, KnowledgeStatus, KnowledgeTypeKey } from '@/types'

import Link from 'next/link'
import { Building2, Globe, Hash, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ICONS } from '@/lib/icons'
import { daysSince } from '@/lib/format'
import { Badge } from '@/components/ds/badge'

import {
    CATEGORY_BY_SLUG,
    CONFIDENTIALITY_META,
    STATUS_META,
    TYPE_BY_KEY,
    labelTitle
} from '@/constants/taxonomy'

export const TypeBadge: FC<{ type: KnowledgeTypeKey, className?: string }> = ({ type, className }): ReactNode => {
    const meta = TYPE_BY_KEY[type]
    const Icon = ICONS[meta.icon]

    return (
        <Badge tone='outline' className={className}>
            <Icon aria-hidden='true' />
            {meta.title}
        </Badge>
    )
}

export const CategoryBadge: FC<{ category: string, className?: string }> = ({ category, className }): ReactNode => {
    const meta = CATEGORY_BY_SLUG[category]

    if (!meta) return null

    return (
        <Badge tone='primary' className={className}>
            {meta.title}
        </Badge>
    )
}

export const StatusBadge: FC<{ status: KnowledgeStatus, size?: 'sm' | 'md' }> = ({ status, size = 'sm' }): ReactNode => {
    const meta = STATUS_META[status]

    return (
        <Badge tone={meta.tone} size={size} dot title={meta.description}>
            {meta.title}
        </Badge>
    )
}

const CONFIDENTIALITY_ICONS = {
    public: Globe,
    internal: Building2,
    confidential: Lock
}

export const ConfidentialityBadge: FC<{ level: Confidentiality }> = ({ level }): ReactNode => {
    const Icon = CONFIDENTIALITY_ICONS[level]

    return (
        <Badge tone={level === 'confidential' ? 'navy' : 'neutral'} title={CONFIDENTIALITY_META[level].description}>
            <Icon aria-hidden='true' />
            {CONFIDENTIALITY_META[level].title}
        </Badge>
    )
}

/** «جدید» for pages published in the last week, «به‌روزشده» for recent edits of older pages. */
export const FreshnessBadge: FC<{ article: Article }> = ({ article }): ReactNode => {
    if (daysSince(article.publishedAt) <= 7) {
        return <Badge tone='success' dot>جدید</Badge>
    }

    if (daysSince(article.updatedAt) <= 7) {
        return <Badge tone='success' dot>به‌روزشده</Badge>
    }

    return null
}

interface LabelChipProps {
    label: string
    className?: string
}

export const LabelChip: FC<LabelChipProps> = ({ label, className }): ReactNode => {
    return (
        <Link
            href={`/search?label=${encodeURIComponent(label)}`}
            title={`برچسب: ${label}`}
            className={cn(
                'relative z-10 inline-flex h-7 items-center gap-1 rounded-full border border-input bg-card px-2.5 text-caption text-foreground/80 transition-colors hover:border-primary/40 hover:bg-primary-soft hover:text-primary-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                className
            )}
        >
            <Hash className='size-3 text-subtle' aria-hidden='true' />
            {labelTitle(label)}
        </Link>
    )
}
